import { useQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { GET_POST_BY_ID_QUERY } from '../../../graphql/resolvers/postQueries/getPostById.gql';
import type { StoryAnalysisLike } from '../../../helpers/postScoreAndMetrics.helpers';
import AnalysisDashboard from './AnalysisDashboard';
import './post-analysis.scss';

interface Props {
  postId: string;
}

/**
 * TRIL-263 / TRIL-367: thin fetch-wrapper for the per-post Story Analysis page.
 * Resolves the post, then hands its analysis to the shared <AnalysisDashboard>
 * (which the reply analysis page also uses).
 */
export default function PostAnalysis({ postId }: Props) {
  const { data, loading, error } = useQuery(GET_POST_BY_ID_QUERY, {
    variables: { postId },
    fetchPolicy: 'cache-and-network',
    skip: !postId,
  });

  // Origin-aware Back: entry points that arrive from a list (e.g. a feed card)
  // pass the originating location via router state (TRIL-288), so Back returns
  // there. Default to the post detail page for direct loads, refreshes, and
  // entry from the post detail page itself.
  const location = useLocation();
  const backFrom = (location.state as { from?: string } | null)?.from;
  const backTo = backFrom ?? `/post/${postId}`;
  const backLabel = backFrom ? 'Back' : 'Back to post';

  const post = data?.getPostById;

  if (loading && !post) {
    return (
      <div className="POST__ANALYSIS POST__ANALYSIS--state">
        Loading analysis…
      </div>
    );
  }
  if (error || !post) {
    return (
      <div className="POST__ANALYSIS POST__ANALYSIS--state">
        Couldn't load this post's analysis.
      </div>
    );
  }

  return (
    <AnalysisDashboard
      storyId={postId}
      storyAnalysis={post.storyAnalysis as StoryAnalysisLike}
      baseScore={post.score ?? null}
      replyCount={post.replyCount ?? 0}
      authorId={post.authorId}
      backTo={backTo}
      backLabel={backLabel}
      notAnalyzedLabel="This post hasn't been analyzed yet."
    />
  );
}
